import { z } from 'zod';
import { RealmSchema } from './auth';

/* ------------------------------------------------------------------ */
/* Edge and app integrity (W24)                                        */
/*                                                                     */
/* Play Integrity (Android) + App Attest (iOS, dormant) verified on    */
/* the server against a single-use server nonce, cached as a signed    */
/* integrity pass; PII encryption at rest behind a Vault-shaped        */
/* transit port; a scrape guard for menus / discovery / public reads.  */
/* ------------------------------------------------------------------ */

/** Apps send the integrity pass on sensitive calls. */
export const INTEGRITY_HEADER = 'x-capsico-integrity';
/** Set by the Cloudflare transform rule for API requests from outside India and Nepal. */
export const GEO_OUTSIDE_HEADER = 'x-capsico-geo-outside';
/** Rotating discovery token (5-minute sliding window, multi-use) for guest browsing. */
export const DISCOVERY_TOKEN_HEADER = 'x-capsico-discovery';

/* ---------------- Apps and platforms ---------------- */

export const INTEGRITY_APPS = ['customer', 'partner', 'rider', 'mi'] as const;
export const IntegrityAppSchema = z.enum(INTEGRITY_APPS);
export type IntegrityApp = z.infer<typeof IntegrityAppSchema>;

/** Android package / iOS bundle id per app. */
export const INTEGRITY_APP_IDS: Record<IntegrityApp, string> = {
  customer: 'com.capsico.customer',
  partner: 'com.capsico.partner',
  rider: 'com.capsico.rider',
  mi: 'com.capsico.mi',
};

export const INTEGRITY_PLATFORMS = ['android', 'ios'] as const;
export const IntegrityPlatformSchema = z.enum(INTEGRITY_PLATFORMS);
export type IntegrityPlatform = z.infer<typeof IntegrityPlatformSchema>;

export const INTEGRITY_MODES = ['off', 'shadow', 'enforce'] as const;
export const IntegrityModeSchema = z.enum(INTEGRITY_MODES);
export type IntegrityMode = z.infer<typeof IntegrityModeSchema>;

export const INTEGRITY_MODE_LABELS: Record<IntegrityMode, string> = {
  off: 'Off — nothing is checked',
  shadow: 'Shadow — checked and logged, never blocks',
  enforce: 'Enforce — failed checks are refused',
};

/** Play Integrity device levels, weakest first. */
export const DEVICE_VERDICT_LEVELS = ['basic', 'device', 'strong'] as const;
export const DeviceVerdictLevelSchema = z.enum(DEVICE_VERDICT_LEVELS);
export type DeviceVerdictLevel = z.infer<typeof DeviceVerdictLevelSchema>;

export const INTEGRITY_VERDICTS = [
  /** Every check passed. */
  'pass',
  /** A check failed (unrecognised app, weak device, stale token, nonce mismatch, bad chain). */
  'fail',
  /** Google / Apple could not be reached: allowed by design (fail open), never blocks. */
  'unverified_provider_down',
] as const;
export const IntegrityVerdictSchema = z.enum(INTEGRITY_VERDICTS);
export type IntegrityVerdict = z.infer<typeof IntegrityVerdictSchema>;

export const INTEGRITY_VERDICT_LABELS: Record<IntegrityVerdict, string> = {
  pass: 'Passed',
  fail: 'Failed',
  unverified_provider_down: 'Provider unreachable (allowed)',
};

/** Short reason codes recorded with a verdict (shown on the device lookup). */
export const INTEGRITY_REASONS = [
  'nonce_unknown',
  'nonce_reused',
  'nonce_mismatch',
  'nonce_wrong_device',
  'package_mismatch',
  'app_not_recognized',
  'app_unlicensed',
  'device_below_policy',
  'device_no_verdict',
  'token_stale',
  'token_invalid',
  'provider_down',
  'provider_error',
  'attestation_format',
  'attestation_chain',
  'attestation_nonce',
  'attestation_key_id',
  'attestation_app_id',
  'attestation_counter',
  'attestation_aaguid',
  'assertion_unknown_key',
  'assertion_signature',
  'assertion_counter',
  'assertion_app_id',
  'platform_dormant',
  'not_configured',
] as const;
export const IntegrityReasonSchema = z.enum(INTEGRITY_REASONS);
export type IntegrityReason = z.infer<typeof IntegrityReasonSchema>;

/* ---------------- App API: nonce → verify → pass ---------------- */

export const IntegrityNonceInputSchema = z.object({
  app: IntegrityAppSchema,
  platform: IntegrityPlatformSchema,
});
export type IntegrityNonceInput = z.infer<typeof IntegrityNonceInputSchema>;

export const IntegrityNonceResultSchema = z.object({
  /** Server mode: apps do nothing at all when it is "off". */
  mode: IntegrityModeSchema,
  /** base64url, 32 random bytes. Single use, 5 minutes. Null when mode is off. */
  nonce: z.string().nullable(),
  expiresInSeconds: z.number().int().min(0),
  /** Google Cloud project number for the Play Integrity request (not a secret). */
  cloudProjectNumber: z.string().nullable(),
  /** iOS is built but dormant until the iOS apps exist. */
  platformActive: z.boolean(),
});
export type IntegrityNonceResult = z.infer<typeof IntegrityNonceResultSchema>;

const Base64 = z
  .string()
  .min(16)
  .max(20_000)
  .regex(/^[A-Za-z0-9+/_=-]+$/, 'Expected base64');

export const IntegrityVerifyInputSchema = z.discriminatedUnion('platform', [
  z.object({
    platform: z.literal('android'),
    app: IntegrityAppSchema,
    nonce: z.string().min(40).max(64),
    /** Play Integrity token from requestIntegrityToken(nonce). */
    token: z.string().min(16).max(20_000),
  }),
  z.object({
    platform: z.literal('ios'),
    app: IntegrityAppSchema,
    nonce: z.string().min(40).max(64),
    /** App Attest key identifier (base64). */
    keyId: Base64,
    /** First run: attestation object from attestKey(). */
    attestation: Base64.optional(),
    /** Later runs: assertion from generateAssertion(). */
    assertion: Base64.optional(),
  }),
]);
export type IntegrityVerifyInput = z.infer<typeof IntegrityVerifyInputSchema>;

export const IntegrityVerifyResultSchema = z.object({
  verdict: IntegrityVerdictSchema,
  reasons: z.array(IntegrityReasonSchema),
  /** Signed pass for the X-Capsico-Integrity header; null when the verdict failed. */
  pass: z.string().nullable(),
  /** ISO time the pass and the device verdict expire (6 h). */
  expiresAt: z.string().nullable(),
  mode: IntegrityModeSchema,
});
export type IntegrityVerifyResult = z.infer<typeof IntegrityVerifyResultSchema>;

/* ---------------- Capsico OS › Security ---------------- */

export const SecurityRangeQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).default(7),
});
export type SecurityRangeQuery = z.infer<typeof SecurityRangeQuerySchema>;

export const VerdictMixRowSchema = z.object({
  app: IntegrityAppSchema,
  platform: IntegrityPlatformSchema,
  pass: z.number().int(),
  fail: z.number().int(),
  providerDown: z.number().int(),
});
export type VerdictMixRow = z.infer<typeof VerdictMixRowSchema>;

export const IntegrityRuleCountSchema = z.object({
  rule: z.string(),
  wouldBlock: z.number().int(),
  blocked: z.number().int(),
  missingPass: z.number().int(),
});
export type IntegrityRuleCount = z.infer<typeof IntegrityRuleCountSchema>;

export const IntegrityOverviewSchema = z.object({
  flagOn: z.boolean(),
  mode: IntegrityModeSchema,
  modeVersion: z.number().int().nullable(),
  minDeviceVerdict: DeviceVerdictLevelSchema,
  providers: z.object({
    android: z.enum(['configured', 'not_configured']),
    ios: z.enum(['dormant', 'configured', 'not_configured']),
  }),
  days: z.number().int(),
  verdictMix: z.array(VerdictMixRowSchema),
  /** Requests a shadow run would have refused, per sensitive rule. */
  rules: z.array(IntegrityRuleCountSchema),
  wouldBlockTotal: z.number().int(),
  blockedTotal: z.number().int(),
  devicesVerified: z.number().int(),
});
export type IntegrityOverview = z.infer<typeof IntegrityOverviewSchema>;

export const SetIntegrityModeInputSchema = z.object({
  mode: IntegrityModeSchema,
  /** Null when no global override exists yet. */
  expectedVersion: z.number().int().min(1).nullable(),
  reason: z.string().trim().min(10, 'Say why (at least 10 characters)').max(200),
  /** The person typed the mode name to confirm (required for enforce). */
  confirm: z.string().trim().max(20),
});
export type SetIntegrityModeInput = z.infer<typeof SetIntegrityModeInputSchema>;

export const DeviceLookupQuerySchema = z.object({
  /** A device id (hashed server-side), a device hash, or an identity id. */
  q: z.string().trim().min(8).max(80),
});
export type DeviceLookupQuery = z.infer<typeof DeviceLookupQuerySchema>;

export const DeviceIntegrityRowSchema = z.object({
  id: z.uuid(),
  deviceHash: z.string(),
  realm: RealmSchema.nullable(),
  subjectId: z.uuid().nullable(),
  app: IntegrityAppSchema,
  platform: IntegrityPlatformSchema,
  verdict: IntegrityVerdictSchema,
  deviceLevel: z.enum(['none', 'basic', 'device', 'strong']).nullable(),
  reasons: z.array(z.string()),
  verifiedAt: z.string(),
  expiresAt: z.string(),
});
export type DeviceIntegrityRow = z.infer<typeof DeviceIntegrityRowSchema>;

export const IntegrityEventRowSchema = z.object({
  id: z.number().int(),
  at: z.string(),
  rule: z.string(),
  mode: IntegrityModeSchema,
  outcome: z.enum(['would_block', 'blocked', 'allowed_provider_down']),
  reason: z.string(),
  realm: RealmSchema.nullable(),
  geoOutside: z.boolean(),
});
export type IntegrityEventRow = z.infer<typeof IntegrityEventRowSchema>;

export const DeviceLookupResultSchema = z.object({
  deviceHash: z.string().nullable(),
  verdicts: z.array(DeviceIntegrityRowSchema),
  events: z.array(IntegrityEventRowSchema),
});
export type DeviceLookupResult = z.infer<typeof DeviceLookupResultSchema>;

export const SCRAPE_KEY_KINDS = ['subject', 'device', 'token', 'ip'] as const;
export const ScrapeKeyKindSchema = z.enum(SCRAPE_KEY_KINDS);
export type ScrapeKeyKind = z.infer<typeof ScrapeKeyKindSchema>;

export const ScrapeOffenderSchema = z.object({
  keyHash: z.string(),
  keyKind: ScrapeKeyKindSchema,
  strikes: z.number().int(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  lastPath: z.string(),
  geoOutside: z.boolean(),
  slowUntil: z.string().nullable(),
});
export type ScrapeOffender = z.infer<typeof ScrapeOffenderSchema>;

export const ScrapeOffenderQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(30).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type ScrapeOffenderQuery = z.infer<typeof ScrapeOffenderQuerySchema>;

export const ScrapeOverviewSchema = z.object({
  flagOn: z.boolean(),
  limits: z.object({
    per10s: z.number().int(),
    per10min: z.number().int(),
    ipPerMinute: z.number().int(),
    slowLaneMs: z.number().int(),
  }),
  offenders: z.array(ScrapeOffenderSchema),
});
export type ScrapeOverview = z.infer<typeof ScrapeOverviewSchema>;

/* ---------------- PII at rest ---------------- */

export const PII_FIELDS = [
  'identities.phone',
  'customer_addresses.location',
  'customer_addresses.house',
  'customer_addresses.floor',
  'customer_addresses.landmark',
  'customer_addresses.receiver_phone',
  'mi_prospects.phone',
] as const;
export const PiiFieldSchema = z.enum(PII_FIELDS);
export type PiiField = z.infer<typeof PiiFieldSchema>;

export const PII_FIELD_LABELS: Record<PiiField, string> = {
  'identities.phone': 'Sign-in phone numbers',
  'customer_addresses.location': 'Exact address pin',
  'customer_addresses.house': 'House / flat',
  'customer_addresses.floor': 'Floor',
  'customer_addresses.landmark': 'Landmark',
  'customer_addresses.receiver_phone': 'Receiver phone',
  'mi_prospects.phone': 'Prospect owner phone',
};

export const PiiFieldStatusSchema = z.object({
  field: PiiFieldSchema,
  label: z.string(),
  rows: z.number().int(),
  encrypted: z.number().int(),
  plainOnly: z.number().int(),
  onOldKey: z.number().int(),
  /** Clear column already emptied by job:pii-clear-plain. */
  plainCleared: z.boolean(),
  /** False while readers of the clear column remain (see the W24 runbook). */
  clearable: z.boolean(),
  blindIndex: z.boolean(),
});
export type PiiFieldStatus = z.infer<typeof PiiFieldStatusSchema>;

export const PiiJobRunSchema = z.object({
  id: z.number().int(),
  job: z.enum(['backfill', 'rewrap', 'clear_plain']),
  field: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  processed: z.number().int(),
  failed: z.number().int(),
  dryRun: z.boolean(),
});
export type PiiJobRun = z.infer<typeof PiiJobRunSchema>;

/** Key status only — never key material. */
export const PiiKeyStatusSchema = z.object({
  flagOn: z.boolean(),
  provider: z.enum(['local', 'vault', 'none']),
  activeVersion: z.number().int().nullable(),
  versions: z.array(z.number().int()),
  blindIndexReady: z.boolean(),
  writePlain: z.boolean(),
  fields: z.array(PiiFieldStatusSchema),
  recentRuns: z.array(PiiJobRunSchema),
  /** Bank accounts were already sealed by W4 / W12 / W20 (AUTH_ENCRYPTION_KEY). */
  sealedElsewhere: z.array(z.string()),
});
export type PiiKeyStatus = z.infer<typeof PiiKeyStatusSchema>;

export const PII_REVEAL_SUBJECTS = ['identity_phone', 'address', 'prospect_phone'] as const;
export const PiiRevealSubjectSchema = z.enum(PII_REVEAL_SUBJECTS);
export type PiiRevealSubject = z.infer<typeof PiiRevealSubjectSchema>;

export const PiiRevealInputSchema = z.object({
  subject: PiiRevealSubjectSchema,
  id: z.uuid(),
  reason: z.string().trim().min(10, 'Say why (at least 10 characters)').max(300),
});
export type PiiRevealInput = z.infer<typeof PiiRevealInputSchema>;

export const PiiRevealResultSchema = z.object({
  subject: PiiRevealSubjectSchema,
  id: z.uuid(),
  /** Field → clear value. Shown once; the page never caches it. */
  values: z.record(z.string(), z.string().nullable()),
  revealedAt: z.string(),
});
export type PiiRevealResult = z.infer<typeof PiiRevealResultSchema>;

export const PiiRevealLogRowSchema = z.object({
  id: z.number().int(),
  at: z.string(),
  actor: z.string(),
  actorName: z.string().nullable(),
  subject: PiiRevealSubjectSchema,
  subjectId: z.string(),
  reason: z.string(),
});
export type PiiRevealLogRow = z.infer<typeof PiiRevealLogRowSchema>;

export const PiiRevealLogQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type PiiRevealLogQuery = z.infer<typeof PiiRevealLogQuerySchema>;
